const normalizeFamilyName = (value) => String(value || "").trim();

const extractLeafItems = (nodes = []) =>
  (Array.isArray(nodes) ? nodes : [])
    .flatMap((node) => {
      const name = normalizeFamilyName(node?.nombre);
      const children = Array.isArray(node?.hijos) ? node.hijos : [];
      if (!children.length) {
        return name ? [{
          id: Number(node?.id || 0),
          name,
        }] : [];
      }
      return extractLeafItems(children);
    })
    .filter(Boolean);

const mapFamilyGroups = (familyNode) => {
  const children = Array.isArray(familyNode?.hijos) ? familyNode.hijos : [];

  return children
    .map((child) => ({
      id: Number(child?.id || 0),
      title: normalizeFamilyName(child?.nombre),
      items: extractLeafItems(child?.hijos),
    }))
    .filter((group) => group.title);
};

export const buildTopbarFamiliesConfig = (response) => {
  const families = Array.isArray(response?.familias) ? response.familias : [];
  const catalogMenu = families
    .map((family) => ({
      id: Number(family?.id || 0),
      name: normalizeFamilyName(family?.nombre),
      groups: mapFamilyGroups(family),
    }))
    .filter((family) => family.name);

  const navItems = catalogMenu.map((family) => family.name);
  const collections = navItems.length ? ["Todos", ...navItems] : [];

  return {
    collections,
    navItems,
    catalogMenu,
  };
};

export const getMenuSectionName = (section) => normalizeFamilyName(section?.name);

export const getMenuSectionId = (section) => Number(section?.id || 0);

export const getMenuGroupTitle = (group) => normalizeFamilyName(group?.title);

export const getMenuGroupId = (group) => Number(group?.id || 0);

export const getMenuGroupItems = (group) =>
  (Array.isArray(group?.items) ? group.items : [])
    .map((item) => (typeof item === "string"
      ? { id: 0, name: normalizeFamilyName(item) }
      : { id: Number(item?.id || 0), name: normalizeFamilyName(item?.name) }))
    .filter((item) => item.name);

const findMenuSection = (menuStructure = [], collection = "") => {
  const normalizedCollection = normalizeFamilyName(collection);
  return (Array.isArray(menuStructure) ? menuStructure : [])
    .find((item) => getMenuSectionName(item) === normalizedCollection) || null;
};

const findMenuGroup = (section, title = "") => {
  const normalizedTitle = normalizeFamilyName(title);
  return (Array.isArray(section?.groups) ? section.groups : [])
    .find((group) => getMenuGroupTitle(group) === normalizedTitle) || null;
};

const findMenuItem = (section, itemName = "") => {
  const normalizedItemName = normalizeFamilyName(itemName);
  const groups = Array.isArray(section?.groups) ? section.groups : [];

  for (const group of groups) {
    const matchedItem = getMenuGroupItems(group).find((item) => item.name === normalizedItemName);
    if (matchedItem) {
      return {
        group,
        item: matchedItem,
      };
    }
  }

  return {
    group: null,
    item: null,
  };
};

export const resolveCatalogSelection = (menuStructure = [], collection = "", subcollection = "") => {
  const section = findMenuSection(menuStructure, collection);
  const fallbackLabel = normalizeFamilyName(subcollection) || normalizeFamilyName(collection) || "Todos";

  if (!section) {
    return {
      mode: "all",
      section: null,
      group: null,
      item: null,
      currentFamilyId: 0,
      selectedLabel: fallbackLabel || "Todos",
      navigationItems: [],
    };
  }

  if (!normalizeFamilyName(subcollection)) {
    const groups = Array.isArray(section?.groups) ? section.groups : [];
    return {
      mode: groups.length ? "section" : "products",
      section,
      group: null,
      item: null,
      currentFamilyId: getMenuSectionId(section),
      selectedLabel: getMenuSectionName(section) || "Todos",
      navigationItems: groups.map((groupItem) => ({
        id: getMenuGroupId(groupItem),
        name: getMenuGroupTitle(groupItem),
        type: "group",
        items: getMenuGroupItems(groupItem),
      })),
    };
  }

  const matchedGroup = findMenuGroup(section, subcollection);
  if (matchedGroup) {
    const items = getMenuGroupItems(matchedGroup);
    return {
      mode: items.length ? "group" : "products",
      section,
      group: matchedGroup,
      item: null,
      currentFamilyId: getMenuGroupId(matchedGroup),
      selectedLabel: getMenuGroupTitle(matchedGroup),
      navigationItems: items.map((item) => ({
        ...item,
        type: "item",
      })),
    };
  }

  const { group, item } = findMenuItem(section, subcollection);
  if (item) {
    return {
      mode: "products",
      section,
      group,
      item,
      currentFamilyId: Number(item.id || 0),
      selectedLabel: item.name,
      navigationItems: [],
    };
  }

  return {
    mode: "products",
    section,
    group: null,
    item: null,
    currentFamilyId: getMenuSectionId(section),
    selectedLabel: fallbackLabel,
    navigationItems: [],
  };
};

export const buildCatalogSelectionBreadcrumb = (selection) => {
  const items = [];

  if (selection?.section?.name) {
    items.push({
      label: selection.section.name,
      collection: selection.section.name,
      subcollection: "",
      familyId: getMenuSectionId(selection.section),
    });
  }

  if (selection?.group) {
    items.push({
      label: getMenuGroupTitle(selection.group),
      collection: selection.section?.name || "",
      subcollection: getMenuGroupTitle(selection.group),
      familyId: getMenuGroupId(selection.group),
    });
  }

  if (selection?.item?.name) {
    items.push({
      label: selection.item.name,
      collection: selection.section?.name || "",
      subcollection: selection.item.name,
      familyId: Number(selection.item.id || 0),
    });
  }

  return items;
};

export const findCatalogFamilyId = (menuStructure = [], collection = "", subcollection = "") => {
  return resolveCatalogSelection(menuStructure, collection, subcollection).currentFamilyId;
};
